import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import './ResultsTable.scss';

import { IApplicationState } from '@store';

import { HOCLoader, HOCTable, InvalidMessage } from '@components';
import { TableColumn } from '@components/HOCTable/Columns/Column';
import { CheckBoxCell } from '@components/TableCells/CheckBoxCell/CheckBoxCell';
import { QCCell } from '@components/TableCells/QCCell/QCCell';
import BasicCell from '@components/TableCells/BasicCell/BasicCell';
import DateCell from '@components/TableCells/DateCell/DateCell';
import LinkCell from '@components/TableCells/LinkCell/LinkCell';

import SampleTypeCell from '@components/TableCells/SampleTypeCell';

import routes from '@configs/routes.json';
import { tables } from '@configs/config.json';

import {
  useTableHook,
  useTableReducerActions,
  TableConfiguration,
  ResultsTableSortableColumnsEnum,
  useTableFilter,
  ITableFilter,
} from '@hooks';

import ResultsCell, { ReportStatus } from '../ResultCell';
import { ResultsTableEmptyMsg } from '../ResultsTableEmptyMsg';
import { ResultsTableNoData } from '../ResultsTableNoData';

import {
  IGetOrderReports,
  IDownloadSampleReport,
} from '../../../../components/orders/OrderDetail';

import { MultiSelectDownload } from '../MultiSelectDownload/MultiSelectDownload';
// needs to be moved into the @services
import { useOrdersAPI } from '@hooks/api/orders/useOrders';
import useToast from '../../../notification/Toast';

import {
  ResultsTableFilter,
  IResultsFilter,
} from '../../components/TableFilter/TableFilter';

function guid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export enum enumResultsTableColumns {
  hidden = 'hidden',
  sampleName = 'sampleName',
  externalSubjectId = 'patientId',
  sampleId = 'sampleId',
  specimenCollectionDate = 'collectionDate',
  type = 'type',
  qcStatus = 'qcStatus',
  resultsDate = 'resultsDate',
  results = 'results',
  description = 'description',
  orderNumber = 'orderNumber',
}

// Results Table - is the encapsulated table
let tableConfiguration: TableConfiguration = {
  api: {
    load: {
      method: 'post',
      route: routes.api.ordertests.search,
    },
    export: {
      method: 'post',
      route: routes.api.ordertests.export,
    },
  },
  filters: {},
  pagination: {
    numTotal: 0,
    offset: 0,
    limit: tables.pageSize,
    numReturned: 0,
  },
  sortedBy: {
    sortColumn: enumResultsTableColumns.resultsDate,
    sortAscending: false,
  },
};
export enum filterItemList {
  finishedDayRange = 'resultsActivity',
  sampleName = 'sampleName',
  description = 'description',
  externalSubjectId = 'externalSubjectId',
  qcStatus = 'qcStatus',
  orderNumber = 'orderNumber',
  specimenNumber = 'specimenNumber',
  resultName = 'resultName',
  pkgId = 'pkgId',
  pkgName = 'pkgName',
}

let filterInitialState: ITableFilter = {
  isOpen: false,
  isDisabled: false,
  activeFilters: {},
  filters: {
    finishedDayRange: '',
    sampleName: '',
    description: '',
    externalSubjectId: '',
    qcStatus: '',
    orderNumber: '',
    specimenNumber: '',
    resultName: '',
    pkgId: '',
    pkgName: '',
  },
};

export enum filterStatuses {
  filterNoData = 'filter no data',
  filterWithData = 'filter with data',
  noFilterNoData = 'no filter no data',
  noFilterWithData = 'no filter with data',
}
interface IResultsTable {
  callbackCount: Function;
}

export interface IResultsTableInfo {
  columns: JSX.Element[];
  rows: JSX.Element[];
  selectedRows: any[];
  isSelectedDownloadDisplay: boolean;
  selectAllMaxCount: number;
  isSelectAll: boolean;
}

export const ResultsTable: React.FC<IResultsTable> = ({ callbackCount }) => {
  const history = useHistory();

  const [state, setState] = useState<IResultsTableInfo>({
    columns: [],
    rows: [],
    selectedRows: [],
    isSelectedDownloadDisplay: false,
    selectAllMaxCount: 0,
    isSelectAll: false,
  });

  const [status, setTableStatus] = useState('');
  const { t } = useTranslation();

  const { downloadReports } = useOrdersAPI();

  const timezone = useSelector(
    (state: IApplicationState) => state.projectPicker.timeZone
  );
  const isSampleLevelDocumentsEnabled = useSelector(
    (state: IApplicationState) => state.projectPicker.hasSampleLevelDocuments
  );

  const activeAccountId = useSelector(
    (state: IApplicationState) => state.projectPicker.activeAccountId
  );

  const activeProjectId = useSelector(
    (state: IApplicationState) => state.projectPicker.activeProjectId
  );

  const toaster = useToast(
    t('results_downloadToast1'),
    t('results_downloadToast2')
  );

  // is mounted hook

  const {
    isOpen: isFilterOpen,
    isDisabled: isFilterIsDisabled,
    filters,
    activeFilters,
    save: saveFilter,
    update: updateFilter,
    clear: clearFilter,
    clearAll: clearAllFilters,
    toggleOpen: openFilter,
    clearSavedFilter,
    toggleDisable: disableFilter,
  } = useTableFilter(filterInitialState);

  const {
    data: tableData,
    sortedBy: tableSortedBy,
    status: tableStatus,
    dispatch: dispatchToTable,
    exportData: tableExportData,
    pagination: tablePagination,
  } = useTableHook(tableConfiguration);

  /*

      TABLE STATUS (based on filters and response data) - which empty message to display

  */

  useEffect(() => {
    if (Object.keys(activeFilters).length > 0) {
      if (tableData.length === 0) {
        setTableStatus(filterStatuses.filterNoData);
      } else {
        setTableStatus(filterStatuses.filterWithData);
      }
    } else {
      tableData.length === 0
        ? setTableStatus(filterStatuses.noFilterNoData)
        : setTableStatus(filterStatuses.filterWithData);
    }
  }, [tableData, activeFilters]);

  /*

      SETUP FILTERS FROM (GET - URL Params)

  */
  useEffect(() => {
    const searchParams: URLSearchParams = new URLSearchParams(
      history.location.search
    );

    let nSort = {
      sortColumn: '',
      sortAscending: false,
    };
    for (const [key, value] of searchParams.entries()) {
      if (Object.keys(filterItemList).includes(key)) {
        filterInitialState.filters[key] = value;
        filterInitialState.activeFilters[key] = value;
        if (key === filterItemList.pkgId || key === filterItemList.pkgName) {
          disableFilter();
        }
      } else {
        if (key === 'sortColumn') {
          Object.assign(nSort, { [key]: value });
        } else {
          Object.assign(nSort, { [key]: value === 'true' ? true : false });
        }
      }
    }

    if (nSort.sortColumn !== '') {
      dispatchToTable({
        type: useTableReducerActions.initSort,
        payload: nSort,
      });
    }
  }, []);

  /*

      PAGINATION CHANGES (update parent)

  */
  useEffect(() => {
    let newFilters = {
      ...activeFilters,
    };
    if (newFilters.pkgName) {
      delete newFilters.pkgName;
    }

    dispatchToTable({
      type: useTableReducerActions.updateFilters,
      payload: newFilters,
    });

    setState({
      ...state,
      isSelectedDownloadDisplay: false,
      isSelectAll: false,
      selectedRows: [],
    });
  }, [activeFilters]);

  useEffect(() => {
    callbackCount(tablePagination);
  }, [tablePagination]);

  /*

      GENERATES TABLE COLUMNS

  */

  // whenever toggled - reload both column and table
  React.useEffect(() => {
    if (activeAccountId && activeProjectId && timezone) {
      const columns = generateColumns();
      const rows = generateRows();
      const maxCount = generateMaxCount();

      setState({
        ...state,
        rows: rows,
        columns: columns,
        selectAllMaxCount: maxCount,
      });
    }
  }, [
    activeAccountId,
    activeProjectId,
    timezone,
    tableData,
    state.isSelectAll,
    state.selectedRows,
    state.selectAllMaxCount,
    isSampleLevelDocumentsEnabled,
  ]);

  /*

      HELPER METHOD for TABLE ROWS (useEffect)

  */
  const isRowSelected = useCallback(
    (id: string) => {
      return state.selectedRows.find((r) => r.orderTestId === id) !== undefined;
    },
    [state]
  );

  const isRowDisabled = useCallback(
    (reports) => {
      return reports.filter(
        (report: any) => report.status === ReportStatus.active
      ).length === 0
        ? true
        : false;
    },
    [state]
  );

  const generateMaxCount = useCallback(() => {
    let maxSelectedCount = 0;

    tableData.forEach(({ reports }) => {
      // is the row selected or disabled
      const isDisabled = isRowDisabled(reports);
      if (!isDisabled) {
        maxSelectedCount++;
      }
    });
    return maxSelectedCount;
  }, [state, tableData]);

  const generateRows = useCallback(() => {
    const rows = tableData.map(
      (
        {
          orderTestSummary: {
            order: { orderNumber },
            orderTest: { sampleName, testType: type, description, id },
            specimen: {
              externalSubjectId: patientId,
              specimenNumber: sampleId,
              collectionDate,
            },
            status: { qcStatus, finished: resultDate, status },
          },
          reports,
        },
        idx
      ) => {
        // is the row selected or disabled
        const isDisabled = isRowDisabled(reports);
        const isSelected = isRowSelected(id);

        return (
          <tr
            key={guid()}
            onClick={() =>
              dispatchToTable({
                type: useTableReducerActions.selectRows,
                payload: sampleId,
              })
            }
            className={isSelected ? 'selected-row' : 't-row'}
          >
            {state.isSelectedDownloadDisplay ? (
              <>
                {!isDisabled ? (
                  <CheckBoxCell
                    key={guid()}
                    value={{
                      type: DocumentType,
                      orderTestId: id,
                      reports: reports,
                    }}
                    isChecked={isSelected}
                    callback={selectCheckRow}
                  />
                ) : (
                  <td></td>
                )}
              </>
            ) : null}

            <BasicCell value={sampleName} truncate={true} isBold={true} />
            <BasicCell value={patientId} truncate={true} isBold={true} />
            <BasicCell value={sampleId} truncate={true} />
            <DateCell date={collectionDate} type='localDate' />
            <SampleTypeCell
              sampleType={type}
              warning={{
                status,
                message: t('orderDetail_test_waiting'),
              }}
            />
            <QCCell status={qcStatus} />
            <DateCell date={resultDate} type='timezone' timeZone={timezone} />
            {isSampleLevelDocumentsEnabled && activeProjectId ? (
              <ResultsCell
                projectId={activeProjectId}
                externalSubjectId={patientId}
                reports={reports}
              />
            ) : null}
            <LinkCell
              text={description}
              url={`orders/${orderNumber}`}
              type='internal'
            />
            <LinkCell
              text={orderNumber}
              url={`orders/${orderNumber}`}
              type='internal'
            />
          </tr>
        );
      }
    );
    return rows;
  }, [state, tableData, activeFilters]);

  const generateColumns = useCallback(() => {
    const { sortColumn, sortAscending } = tableSortedBy;
    let columnsList = [];
    for (const [key, value] of Object.entries(enumResultsTableColumns)) {
      if (key !== enumResultsTableColumns.hidden) {
        const isActive = value === sortColumn ? true : false;
        const isSortable = Object.keys(
          ResultsTableSortableColumnsEnum
        ).includes(value)
          ? true
          : false;
        if (key === 'results' && !isSampleLevelDocumentsEnabled) {
          //do nothing
        } else {
          columnsList.push(
            <TableColumn
              name={t(`results_column_${value}`)}
              isActive={isActive}
              isAscending={sortAscending}
              isSortable={isSortable}
              key={key}
              sort={() =>
                dispatchToTable({
                  type: useTableReducerActions.sort,
                  payload: value,
                })
              }
            />
          );
        }
      } else {
        // if there the select & download is displayed, add extra select all checkbox
        if (state.isSelectedDownloadDisplay) {
          columnsList.push(
            <th key={key} className='select-download'>
              <input
                type='checkbox'
                checked={state.isSelectAll ? true : false}
                onClick={() => selectCheckboxCallback()}
              />
            </th>
          );
        }
      }
    }
    return columnsList;
  }, [state, state.isSelectAll, tableData, activeFilters, tablePagination]);

  /*

      SHOW OR HIDE THE (Select & Download)

  */
  const setSelectAllDownloadCallback = useCallback(() => {
    setState({
      ...state,
      isSelectAll: false,
      selectedRows: [],
      isSelectedDownloadDisplay: !state.isSelectedDownloadDisplay,
    });
  }, [state]);
  /*

      SELECT ALL ROWS

  */
  const selectCheckboxCallback = useCallback(() => {
    // if currently true - then simply reset select rows
    if (state.selectedRows.length === state.selectAllMaxCount) {
      setState({
        ...state,
        isSelectAll: false,
        selectedRows: [],
      });
    }
    // if currently false - then loop through tabledata and select all selectable rows
    else {
      let selectedRows: any[] = [];
      tableData.forEach(
        (
          {
            orderTestSummary: {
              orderTest: { testType: type, id },
            },
            reports,
          },
          idx
        ) => {
          const isDisabled = isRowDisabled(reports);
          if (!isDisabled) {
            selectedRows.push({
              orderTestId: id,
              reports: reports,
              documentType: type,
            });
          }
        }
      );
      setState({
        ...state,
        isSelectAll: true,
        selectedRows: selectedRows,
      });
    }
  }, [state, tableData, tablePagination, activeFilters]);

  /*

      SELECT SINGLE ROW

  */
  const selectCheckRow = useCallback(
    (value): void => {
      let nRows = [...state.selectedRows];
      const isValueFound = nRows.find(
        (t) => t.orderTestId === value.orderTestId
      );

      if (isValueFound) {
        nRows = nRows.filter((r) => r.orderTestId !== value.orderTestId);
      } else {
        nRows.push(value);
      }

      let isAllSelected =
        nRows.length === state.selectAllMaxCount ? true : false;

      setState({
        ...state,
        isSelectAll: isAllSelected,
        selectedRows: [...nRows],
      });
    },
    [state, tableData, tablePagination, activeFilters]
  );

  /*

      DOWNLOAD THE SELECTED ROWS

  */
  const downloadSelected = useCallback(() => {
    let downloadReportsParams: IGetOrderReports = {
      documentTypes: [],
      orderTestIds: [],
    };

    state.selectedRows.forEach((item) => {
      if (item.reports.length > 0) {
        let activeReports = item.reports.filter(
          (r: any) => r.status === 'Active'
        );
        if (activeReports.length > 0) {
          const item: IDownloadSampleReport = activeReports[0] as any;
          downloadReportsParams.documentTypes.push(item.documentType);
          downloadReportsParams.orderTestIds.push(item.orderTestId);
        }
      }
    });

    const uniqueDocumentTypes = new Set(downloadReportsParams.documentTypes);

    downloadReports(
      Array.from(uniqueDocumentTypes),
      downloadReportsParams.orderTestIds
    );
    // toaster show
    toaster.show();
    setState({
      ...state,
      selectedRows: [],
      isSelectAll: false,
      isSelectedDownloadDisplay: false,
    });
  }, [state]);
  /*

      PAGINATE THE TABLE (load more)

  */
  const paginateTable = useCallback(() => {
    setState({
      ...state,
      isSelectAll: false,
    });
  }, [state]);
  /*

      CUSTOM EMPTY MESSAGE

  */
  const showEmptyMsg = () => {
    // if there is a filter set
    if (Object.keys(activeFilters).length > 0) {
      return <ResultsTableNoData />;
    } else {
      return <ResultsTableEmptyMsg />;
    }
  };

  return (
    <div
      id='results-table'
      className='results-table-container'
      data-isopen={state.isSelectedDownloadDisplay ? 'open' : 'closed'}
    >
      {status !== filterStatuses.noFilterNoData ? (
        <>
          <ResultsTableFilter
            isOpen={isFilterOpen}
            isDisabled={isFilterIsDisabled}
            filters={filters}
            activeFilters={activeFilters as IResultsFilter}
            toggleOpen={openFilter}
            toggleDisabled={disableFilter}
            update={updateFilter}
            save={saveFilter}
            clear={clearFilter}
            clearSavedFilter={clearSavedFilter}
            clearAll={clearAllFilters}
          />
          <hr className='m-0' />
        </>
      ) : null}

      <div id='table-header' className='results-controls'>
        <div id='header-left'>
          {isSampleLevelDocumentsEnabled &&
          (status === filterStatuses.filterWithData ||
            status === filterStatuses.noFilterWithData) ? (
            <MultiSelectDownload
              isSelected={state.isSelectedDownloadDisplay}
              callback={setSelectAllDownloadCallback}
              isItems={state.selectedRows.length > 0}
              isDownloadable={downloadSelected}
              cancelDownload={setSelectAllDownloadCallback}
            />
          ) : null}
        </div>
        <div id='header-right'>
          {tableData.length > 0 ? (
            <span>
              <i className='fas fa-arrow-circle-down' />
              <span
                className='export-list fcolor--global-dark'
                onClick={() => tableExportData()}
              >
                {t('results_export_list')}
              </span>
            </span>
          ) : null}
        </div>
      </div>
      <HOCLoader
        isEmpty={tableData.length <= 0}
        customEmpty={showEmptyMsg()}
        customError={
          <InvalidMessage
            title={t('results_error_msg_title')}
            description={t('results_error_msg_description')}
          />
        }
        status={tableStatus}
      >
        <HOCTable
          rows={state.rows}
          columns={state.columns}
          pagination={{ info: tablePagination, dispatch: dispatchToTable }}
          resetSelected={paginateTable}
        ></HOCTable>
        <>{toaster.render}</>
      </HOCLoader>
    </div>
  );
};
